import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { useAuth } from "@/lib/AuthContext";

interface LocationStateWithFrom {
  from?: { pathname?: string };
}

interface SystemSettings {
  corporate_domain: string | null;
}

const ACCESS_KEY = "mes:auth:access";
void ACCESS_KEY; // reserved for future "remember me"

export default function Login() {
  const { t } = useTranslation();
  const { loginByPassword } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationStateWithFrom | null)?.from?.pathname ?? "/";

  const [corporateDomain, setCorporateDomain] = useState<string | null>(null);
  // username — local part when corporate, full string otherwise.
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch corp-domain hint once at mount.  Endpoint is public so it
  // works pre-login.  Failure → silently fall back to free-form login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/v1/system/settings");
        if (!res.ok) return;
        const j = (await res.json()) as SystemSettings;
        if (!cancelled) setCorporateDomain(j.corporate_domain);
      } catch {
        /* offline / dev → free-form input */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Final login string — full email when corporate, raw input otherwise.
  // Also tolerant if the operator types the full email by habit in
  // corporate mode (we strip the @domain suffix client-side).
  function effectiveLogin(): string {
    const s = username.trim();
    if (!corporateDomain) return s;
    if (!s) return "";
    if (s.includes("@")) return s.toLowerCase();
    return `${s}@${corporateDomain}`.toLowerCase();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const login = effectiveLogin();
    if (!login || !password) {
      setError(t("login.errFillBoth"));
      return;
    }
    setBusy(true);
    try {
      await loginByPassword(login, password);
      nav(from, { replace: true });
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : t("login.errFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center p-6">
      <form onSubmit={submit} className="card p-8 w-full max-w-sm space-y-5">
        <header>
          <p className="font-mono text-xs uppercase tracking-widest text-text-muted">
            mes · sign in
          </p>
          <h1 className="text-3xl mt-2">{t("login.title")}</h1>
        </header>

        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {corporateDomain ? t("login.loginLabel") : t("login.loginEmailLabel")}
          </span>
          {corporateDomain ? (
            <div className="mt-1 flex items-stretch rounded-md bg-surface border border-border overflow-hidden focus-within:border-accent">
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ivan.petrov"
                className="flex-1 min-w-0 px-3 py-2 bg-transparent border-0 outline-none font-mono"
              />
              <span className="px-3 py-2 font-mono text-text-muted text-sm whitespace-nowrap select-none border-l border-border bg-surface-alt flex items-center">
                @{corporateDomain}
              </span>
            </div>
          ) : (
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
            />
          )}
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("login.password")}</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
          />
        </label>

        {error && <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>}

        <button type="submit" className="btn btn-primary w-full" disabled={busy}>
          {busy ? t("login.signingIn") : t("login.submit")}
        </button>
      </form>
    </div>
  );
}
